import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import CardComponent from "./CardComponent"; // Importando o novo componente
import companiesData from "./data"; // Dados das empresas

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  background-color: #f9f9f9;
  border-radius: 20px;
`;

const Header = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: #101828;
  margin-bottom: 30px;
  text-transform: uppercase;
`;

const SearchBarContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
`;

const SearchBar = styled.input`
  width: 70%;
  max-width: 600px;
  padding: 14px 18px;
  font-size: 1.1rem;
  border: 2px solid #e0e0e0;
  border-radius: 50px;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:focus {
    border-color: #0066ff;
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 85%;
  }
`;

const SearchButton = styled.button`
  background-color: #0066ff;
  color: white;
  font-size: 1rem;
  padding: 14px 24px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: #004bb5;
    transform: scale(1.05);
  }

  &:active {
    background-color: #003f8c;
    transform: scale(1);
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 12px 22px;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
`;

const FilterDropdown = styled.select`
  padding: 14px 18px;
  font-size: 1rem;
  border-radius: 12px;
  border: 2px solid #e0e0e0;
  background-color: #fff;
  transition: all 0.3s ease;

  &:focus {
    border-color: #0066ff;
    background-color: #f2f6ff;
  }

  &:hover {
    border-color: #004bb5;
  }

  @media (max-width: 768px) {
    width: 100%;
    font-size: 1rem;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
  padding-top: 20px;
`;

const Loader = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #0066ff;
  font-size: 1.5rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 30px;
`;

const PageButton = styled.button`
  background-color: ${({ isActive }) => (isActive ? "#0066ff" : "#e0e0e0")};
  color: ${({ isActive }) => (isActive ? "white" : "#000")};
  font-size: 1rem;
  padding: 10px 15px;
  border: 1px solid #0066ff;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;

  &:hover {
    background-color: ${({ isActive }) => (isActive ? "#004bb5" : "#b0b0b0")};
    transform: scale(1.1);
  }

  &:active {
    background-color: #003f8c;
    transform: scale(1);
  }

  &:disabled {
    background-color: #e0e0e0;
    cursor: not-allowed;
  }
`;

const SearchComponent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [filteredCompanies, setFilteredCompanies] = useState(companiesData);
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage] = useState(6); // Número de cards por página
  const navigate = useNavigate();

  useEffect(() => {
    const filterCompanies = () => {
      setLoading(true);
      const result = companiesData.filter(
        (company) =>
          company.nome.toLowerCase().includes(searchTerm.toLowerCase()) &&
          (selectedLocation ? company.localizacao === selectedLocation : true) &&
          (selectedCategory ? company.categoria === selectedCategory : true)
      );
      setFilteredCompanies(result);
      setLoading(false);
      setCurrentPage(1); // Resetar para a primeira página após filtro
    };

    const timer = setTimeout(() => filterCompanies(), 1500);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedLocation, selectedCategory]);

  const handleCardClick = (companyId) => {
    navigate(`/company-details/${companyId}`);
  };

  // Lógica para paginação
  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(indexOfFirstCompany, indexOfLastCompany);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  return (
    <Container>
      <Header>Encontre as melhores empresas e serviços para você.</Header>
      <SearchBarContainer>
        <SearchBar
          type="text"
          placeholder="Pesquise por empresas ou serviços..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <SearchButton disabled={loading}>Buscar</SearchButton>
      </SearchBarContainer>
      <FiltersContainer>
        <FilterDropdown
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
        >
          <option value="">Localização</option>
          <option value="Lisboa">Lisboa</option>
          <option value="Porto">Porto</option>
          <option value="Braga">Braga</option>
        </FilterDropdown>
        <FilterDropdown
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Categoria de Serviço</option>
          <option value="Consultoria">Consultoria</option>
          <option value="Legal">Legal</option>
          <option value="Tecnologia">Tecnologia</option>
        </FilterDropdown>
      </FiltersContainer>
      <CardsContainer>
        {loading ? (
          <Loader>Carregando...</Loader>
        ) : (
          currentCompanies.map((company) => (
            <CardComponent
              key={company.id}
              company={company}
              onClick={() => handleCardClick(company.id)}
            />
          ))
        )}
      </CardsContainer>
      <PaginationContainer>
        <PageButton
          onClick={() => paginate(1)}
          disabled={currentPage === 1}
        >
          1
        </PageButton>
        <PageButton
          onClick={() => paginate(2)}
          disabled={currentPage === 2}
        >
          2
        </PageButton>
        <PageButton
          onClick={() => paginate(3)}
          disabled={currentPage === 3}
        >
          3
        </PageButton>
        {/* Adicione mais botões de páginas conforme necessário */}
        <PageButton
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Próxima
        </PageButton>
      </PaginationContainer>
    </Container>
  );
};

export default SearchComponent;
